/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useEffect, useState } from 'react';
import { FormControl, FormLabel, Stack, Button, Autocomplete, useTheme, TextField } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useObjectContext } from '@/hooks/useObjectContext';
import { useUpdateDealRowMutation } from '@/features/deals/dealsApi';
import { openDialog } from '@/features/dialog/dialogSlice';
import {
	useGetInsightClaimQuery,
	useRetrieveClaimStatusListQuery,
	useRetrieveFirebaseClaimStatusQuery,
	useUpdateInsightClaimMutation,
} from '@/features/claims/ticketsApi';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { usePipelineContext } from '@/context/pipelineContext';
import { useGetUserDetailsQuery } from '@/features/user/userApi';

export const DealCardStageChange = ({ showLabel, title, minimal = true, showReopen = false }) => {
	const theme = useTheme();
	const dispatch = useDispatch();

	const [showConfirmDialog, setShowConfirmDialog] = useState(false);

	const minimalInputTheme = theme.components.MuiInputBase.styleOverrides.minimal;

	const { dealRow, dealId } = useObjectContext();

	const { stages, ...pipelineQuery } = usePipelineContext(dealRow?.pipeline);

	const currentStage = stages.find((s) => s.id === dealRow?.dealStage);

	const isClaim = dealRow?.pipeline == process.env.REACT_APP_PIPELINE_CLAIMS;
	const isDebtor = dealRow?.pipeline == process.env.REACT_APP_PIPELINE_DEBTORS;

	const { data: insightClaim, ...insightClaimQuery } = useGetInsightClaimQuery(dealId, {
		skip: !isClaim,
	});

	const claim = insightClaim ?? {};

	const { username: email } = useSelector((state) => state.msalAccount.account);

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	//some brokerages have underscores so making sure I only grab the prefix at the start
	const { data: claimStatuses } = useRetrieveFirebaseClaimStatusQuery(
		{ ticketId: dealId, ledger: userDetails?.broker?.ledger.split('__')[0].trim() },
		{ skip: !userDetails?.broker?.ledger },
	);

	const hasPolicies = dealRow?.policies?.length > 0;
	const { data: statusList } = useRetrieveClaimStatusListQuery(null, { skip: !isClaim });

	const isDisabled = currentStage?.closed || currentStage?.completed;

	const [updateDealRow, { isLoading: isUpdatingDealRow }] = useUpdateDealRowMutation();

	const [updateInsightClaim, { isLoading: isLoadingClaimUpdate }] =
		useUpdateInsightClaimMutation();

	const showReopenButton =
		showReopen && isClaim && claim && isDisabled && statusList && !isLoadingClaimUpdate;

	const reopenClaim = async () => {
		setShowConfirmDialog(false);

		const openStatus = claimStatuses.find((stage) => stage.isFirstStage);
		if (!openStatus) return;
		const openStage = stages.find((s) => s.id === openStatus.hubspotStageId);
		if (!openStage) return;

		const completedCalls = [];

		const previousStageId = dealRow?.dealStage;
		const previousStage = stages.find((s) => s.id === previousStageId);
		const previousClosedDate = dealRow?.dateClosed;

		try {
			await updateStage(openStage);
			completedCalls.push('firebase');

			await updateInsightClaim({
				dealId: dealRow.id,
				properties: {
					StatusId: openStatus.insightStageId,
				},
			}).unwrap();
		} catch (error) {
			console.error('Error reopening claim', error);
			if (completedCalls.includes('firebase')) {
				await updateStage(previousStage);
				await updateDealRow({
					dealId: dealRow?.dealId,
					properties: { dateClosed: previousClosedDate ?? null },
				}).unwrap();
			}
		}
	};

	const updateStage = async (newStage) => {
		const isClosingDeal = newStage.completed || newStage.closed;
		const isReopeningDeal = currentStage.completed || currentStage.closed;

		const properties = {
			dealStage: newStage.id,
			...(isClosingDeal && {
				...(newStage.completed && {
					completed: true,
					completedBy: userDetails.userId,
				}),
				closed: true,
				closedBy: userDetails.userId,
				dateClosed: new Date().getTime(),
			}),
			...((isReopeningDeal || !isClosingDeal) && {
				completed: false,
				closed: false,
				dateClosed: null,
				completedBy: null,
				closedBy: null,
			}),
		};

		await updateDealRow({
			dealId: dealRow.dealId,
			docId: dealRow.id,
			properties,
		}).unwrap();

		const closedStatus = claimStatuses.find((stage) => stage.isClosedStage);

		if (isClaim && isClosingDeal && closedStatus) {
			await updateInsightClaim({
				dealId: dealRow.id,
				properties: {
					StatusId: closedStatus?.insightStageId,
				},
			}).unwrap();
		}
	};

	const handleStageChange = async (_, option) => {
		if (!option) return;

		const isClosedLost = Boolean(option.closed) && !option.completed;
		if (isClosedLost && !isClaim && !isDebtor) {
			dispatch(
				openDialog({
					dialogName: 'lossReason',
					data: {
						dealId: dealRow?.dealId ?? dealId,
						docId: dealRow?.id,
						stageId: option.id,
					},
				}),
			);
			return;
		}

		try {
			await updateStage(option);
		} catch (error) {
			console.error('Error updating stage', error);
		}
	};

	const disabled = isUpdatingDealRow || isLoadingClaimUpdate || (isClaim && isDisabled);

	return (
		<FormControl fullWidth>
			{showLabel != false && <FormLabel>{title ?? 'Deal Stage'}</FormLabel>}
			<Autocomplete
				fullWidth
				disabled={disabled}
				loading={pipelineQuery.isLoading || (isClaim && insightClaimQuery.isLoading)}
				isOptionEqualToValue={(option, val) => option?.id === val?.id}
				getOptionLabel={(option) => option?.label ?? ''}
				options={stages}
				value={currentStage ?? null}
				inputValue={currentStage?.label ?? ''}
				filterOptions={(options) => options}
				disableClearable
				onChange={handleStageChange}
				renderOption={(props, option) => {
					return (
						<li {...props} style={{ width: '100%' }} key={`deal-stage-${option.id}`}>
							{option.label}
						</li>
					);
				}}
				getOptionDisabled={(option) =>
					!hasPolicies && !isClaim && (option.completed || option.isEndorsement)
				}
				renderInput={(params) => (
					<TextField
						{...params}
						variant={minimal ? 'standard' : 'outlined'}
						// error={error}
						onKeyDown={(e) => {
							e.preventDefault();
						}}
						slotProps={{
							input: {
								...params.InputProps,
								readOnly: true,

								style: {
									caretColor: 'transparent',
									cursor: 'pointer',
									...(minimal && {
										...(disabled && { backgroundColor: 'transparent' }),
									}),
								},
							},
						}}
						sx={{
							...(minimal && {
								...minimalInputTheme,
								'& .Mui-disabled': {
									backgroundColor: 'transparent',
								},
								// '& .MuiInput-underline:before': {
								// 	borderBottomColor: '#d3d3d3',
								// },
								// '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
								// 	borderBottomColor: '#bdbdbd',
								// },
							}),
							'& .MuiInput-input': {
								color: theme.palette.primary.main,
							},
							cursor: 'pointer',
						}}
					/>
				)}
				sx={{
					cursor: 'pointer',
					'& .MuiInputBase-root': {
						cursor: 'pointer',
					},
					'& input': {
						cursor: 'pointer',
					},
					...(minimal && {
						...minimalInputTheme,
						'& .Mui-disabled': {
							backgroundColor: 'transparent',
						},
						'& .MuiInputBase-root': {
							backgroundColor: 'transparent',
						},
					}),
				}}
			/>
			{showReopenButton && (
				<Stack direction='row' justifyContent={'flex-end'}>
					<Button size='small' variant='text' onClick={() => setShowConfirmDialog(true)}>
						Reopen Claim
					</Button>
				</Stack>
			)}
			{showConfirmDialog && (
				<ConfirmDialog
					openDialog={showConfirmDialog}
					handleClose={() => setShowConfirmDialog(false)}
					handleConfirm={reopenClaim}
					actionName={'reopen claim'}
					canBeUndone={true}
				/>
			)}
		</FormControl>
	);
};
